import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Pill } from "@/pages/admin/AdminLayout";
import { Card } from "@/pages/admin/email/EmailLayout";

const Stat = ({ label, value, testid }) => (
  <div className="bg-white border border-line px-5 py-6" data-testid={testid}>
    <div className="display text-[34px] leading-none text-charcoal">{value ?? "—"}</div>
    <div className="text-[10.5px] tracking-[0.16em] uppercase text-[#8A867F] mt-2.5">{label}</div>
  </div>
);

export default function EmailOverview() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/admin/email/overview").then((r) => setData(r.data));
  }, []);

  const s = data?.sending || {};

  return (
    <div className="space-y-8" data-testid="email-overview">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Total subscribers" value={data?.total} testid="stat-total" />
        <Stat label="Active (confirmed)" value={data?.active} testid="stat-active" />
        <Stat label="Awaiting confirmation" value={data?.pending} testid="stat-pending" />
        <Stat label="Unsubscribed" value={data?.unsubscribed} testid="stat-unsubscribed" />
        <Stat label="New this month" value={data?.new_this_month} testid="stat-new-month" />
        <Stat label="Campaigns sent" value={data?.campaigns_sent} testid="stat-campaigns-sent" />
        <Stat label="Campaigns saved" value={data?.campaigns_total} testid="stat-campaigns-total" />
        <Stat label="Welcome emails sent" value={data?.welcome_sent} testid="stat-welcome-sent" />
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        <Card title="Sending status" testid="email-sending-status">
          <div className="space-y-3">
            {[
              ["Signup collection", s.collection_enabled],
              ["Confirmation emails", s.confirmation_sending_enabled],
              ["Welcome autoresponder", s.autoresponder_enabled],
              ["Campaign sending", s.campaign_sending_enabled],
            ].map(([label, on]) => (
              <div key={label} className="flex items-center justify-between">
                <span className="text-[13.5px] font-light text-[#5A5A5A]">{label}</span>
                <Pill tone={on ? "green" : "yellow"}>{on ? "on" : "off"}</Pill>
              </div>
            ))}
            <div className="flex items-center justify-between">
              <span className="text-[13.5px] font-light text-[#5A5A5A]">Marketing provider</span>
              <Pill tone={s.marketing_provider ? "green" : "yellow"}>
                {s.marketing_provider || "not connected"}
              </Pill>
            </div>
          </div>
          <p className="text-[12.5px] font-light text-[#8A867F] leading-relaxed mt-5">
            Switches live in{" "}
            <Link to="/admin/email/settings" className="text-sage">
              Email Settings
            </Link>
            . Confirmation and welcome messages use Emergent managed email. Bulk campaigns need a
            marketing provider connected first.
          </p>
        </Card>

        <Card title="Recent signups" testid="email-recent-subscribers">
          {(data?.recent_subscribers || []).length === 0 && (
            <p className="text-[13px] font-light text-[#8A867F]">No subscribers yet.</p>
          )}
          {(data?.recent_subscribers || []).map((r) => (
            <div
              key={r.id}
              className="flex items-baseline justify-between py-2.5 border-b border-line last:border-0"
            >
              <span className="text-[13px] font-light text-[#5A5A5A] break-all pr-3">{r.email}</span>
              <span className="flex items-center gap-2 whitespace-nowrap">
                <Pill tone={r.status === "active" ? "green" : r.status === "pending" ? "yellow" : "neutral"}>
                  {r.status}
                </Pill>
                <span className="text-[11.5px] text-[#8A867F]">
                  {String(r.created_at || "").slice(0, 10)}
                </span>
              </span>
            </div>
          ))}
        </Card>
      </div>

      <Card title="Recent email activity" testid="email-recent-sends">
        {(data?.recent_sends || []).length === 0 && (
          <p className="text-[13px] font-light text-[#8A867F]">Nothing sent yet.</p>
        )}
        {(data?.recent_sends || []).map((r) => (
          <div
            key={r.id}
            className="flex items-baseline justify-between py-2.5 border-b border-line last:border-0"
          >
            <span className="text-[13px] font-light text-[#5A5A5A] break-all pr-3">
              {r.kind} — {r.email}
              {r.error ? <span className="text-[#991B1B]"> ({r.error})</span> : null}
            </span>
            <span className="flex items-center gap-2 whitespace-nowrap">
              <Pill tone={r.status === "sent" ? "green" : r.status === "held" ? "yellow" : "red"}>
                {r.status}
              </Pill>
              <span className="text-[11.5px] text-[#8A867F]">
                {String(r.created_at || "").slice(0, 10)}
              </span>
            </span>
          </div>
        ))}
      </Card>
    </div>
  );
}
